import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2Icon } from 'lucide-react';

import { cn } from '@citylytics/ui-web/shared/utils';

const buttonVariants = cva(
  [
    // Box.
    'group/button inline-flex shrink-0 items-center justify-center',
    // Text.
    'text-sm font-medium whitespace-nowrap select-none',
    // Look.
    'cursor-pointer rounded-lg border border-transparent bg-clip-padding outline-none',
    // Motion.
    'transition-all',
    // Hover and press.
    'active:not-aria-[haspopup]:translate-y-px',
    // Focus.
    'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
    // Invalid.
    'aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20',
    // Disabled.
    'disabled:pointer-events-none disabled:opacity-50',
    // Children.
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    // Dark mode.
    'dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40',
  ],
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/80',
        outline:
          'border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
        ghost:
          'hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50',
        destructive:
          'bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40',
        link: 'text-primary underline-offset-4 hover:underline',
        // A button that reads as an inline link (an action next to a TextLink, like Delete).
        'link-destructive': 'text-error underline-offset-4 hover:text-error/80 hover:underline',
        // A quiet icon button that turns red on hover, for removing a row.
        'ghost-destructive': 'text-ink-muted hover:bg-error-subtle hover:text-error',
        // The product's own call to action; apps used to restyle `default` into this by hand.
        brand: 'rounded-xl bg-brand text-brand-foreground hover:bg-brand-hover',
        // A translucent white button on the Emerald sidebar (language, log out).
        'on-brand':
          'rounded-xl border-brand-foreground/40 bg-brand-foreground/20 text-brand-foreground hover:bg-brand-foreground/30 hover:text-brand-foreground aria-expanded:bg-brand-foreground/30',
        // A row in a pick-one list (the roles beside their editor), on a glass card. The chosen
        // row (`aria-current`) lifts to solid white, which only reads against the glass behind it.
        item: 'w-full justify-between rounded-xl text-left font-normal text-ink hover:bg-surface/80 aria-[current=true]:bg-surface aria-[current=true]:font-medium aria-[current=true]:text-brand aria-[current=true]:shadow-sm',
      },
      size: {
        default:
          'h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: 'h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        icon: 'size-8',
        'icon-xs':
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        'icon-sm':
          'size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg',
        'icon-lg': 'size-9',
        // No box: for `link` variants inline in text or a row of actions.
        inline: 'h-auto gap-1 p-0',
        // The `item` variant's row: as tall as its content, with room for a label and a badge.
        item: 'h-auto gap-3 px-3 py-2.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  loading = false,
  disabled,
  children,
  ...props
}: ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    loading?: boolean;
  }) {
  return (
    <ButtonPrimitive
      {...props}
      data-slot="button"
      data-loading={loading || undefined}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size }), className)}
    >
      {loading ? <Loader2Icon className="size-4 animate-spin" aria-hidden="true" /> : null}
      {children}
    </ButtonPrimitive>
  );
}

export { Button, buttonVariants };
